import React from 'react'
import PropTypes from 'prop-types'
import {Link, useParams} from 'react-router-dom'
import {Flex, Box, Text, Button, SimpleGrid, Stack, useMultiStyleConfig} from '@chakra-ui/react'
import {
    getInfoLabelVariantByOmsStatus,
    getOrderStatus,
    getPaymentMethodLabel,
    isOrderExported,
} from '../utils'
import {FormattedMessage, useIntl} from 'react-intl'
import {remCalc} from '../../../../theme/_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import routingObject from '../../../../utils/static-routes'
import {useCustomPreferenceMarketless} from '../../../../hooks/use-custom-preference'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {SFCC_ORDER_STATUS} from '../../constant'
import {isPecualiarAddressFormCountry} from '../../../../utils/peculiar-address-form'
import InformativeLabel from '../../../../components/informative-label/partials/InformativeLabel'

const OrderInfo = ({order, onCancelRequest}) => {
    const {formatDate, formatMessage} = useIntl()
    const {buildUrl, locale} = useMultiSite()
    const params = useParams()
    const returnSettings = useCustomPreferenceMarketless('showReturnsAllowed')
    const paymentMethodLabels = useCustomPreferenceMarketless('paymentMethodLabels')
    const styles = useMultiStyleConfig('Orders', {
        returnSettings: returnSettings,
    })
    const deliveryMethod = order?.deliveryInfo?.OrderDeliveryMethod?.Name
    const deliveryInfo = order?.deliveryInfo
    const billingInfo = order?.billingInfo
    const localeId = locale?.alias
    const isPeculiarAddressFormCountry = isPecualiarAddressFormCountry(locale)
    const fullName = isPeculiarAddressFormCountry
        ? `${order?.lastName} ${order?.firstName}`
        : `${order?.firstName} ${order?.lastName}`
    const navigate = useNavigation()
    const handleReturnPolicy = () => {
        navigate(locale?.returnPolicy || '/Return')
    }

    const deliveryAddress = isPeculiarAddressFormCountry
        ? `${deliveryInfo?.DeliverToName} - ${deliveryInfo?.DeliverToCountryCode} - ${deliveryInfo?.DeliverToPostalCode}- ${deliveryInfo?.DeliverToStreet} - ${deliveryInfo?.DeliverToCity} `
        : `${deliveryInfo?.DeliverToName} - ${deliveryInfo?.DeliverToStreet} - ${deliveryInfo?.DeliverToCity} - ${deliveryInfo?.DeliverToPostalCode} - ${deliveryInfo?.DeliverToCountryCode}`
    const billingAddress = isPeculiarAddressFormCountry
        ? `${fullName} - ${billingInfo?.country} - ${billingInfo?.postalCode} - ${billingInfo?.street} - ${billingInfo?.city}`
        : `${fullName} - ${billingInfo?.street} - ${billingInfo?.city} - ${billingInfo?.postalCode} - ${billingInfo?.country}`

    const paymentType = getPaymentMethodLabel(order?.paymentMethod, paymentMethodLabels)

    // Extract shipment statuses
    const shipmentStatuses = order?.fulfillmentOrder?.map((shipment) => shipment?.Status) || []

    const hasUnassignedOrderItems = (() => {
        const allOrderItems = order?.orderItems || []
        const allFulfillmentLineItems =
            order?.fulfillmentOrder?.flatMap(
                (shipment) => shipment?.FulfillmentOrderLineItems?.records || [],
            ) || []
        const assignedOrderItemIds = new Set(
            allFulfillmentLineItems.map((lineItem) => lineItem?.OrderItemSummaryId).filter(Boolean),
        )

        return allOrderItems.some((item) => !assignedOrderItemIds.has(item?.Id))
    })()

    // Pass shipmentStatuses to getOrderStatus
    const {status, message} = getOrderStatus(order?.status, false, shipmentStatuses, {
        hasUnassignedOrderItems,
    })

    const translatedStatus = message && formatMessage(message)

    const isOrderCancellable =
        !!order &&
        !!order?.status &&
        !isOrderExported(order?.status) &&
        order?.status !== 'Export Error'
    const cancellationDate = order?.cancelationDate

    const deliveryCountryCode = deliveryInfo?.DeliverToCountryCode?.toLowerCase()

    const subscriptionItems = order?.orderItems?.filter(
        (item) => item.PurchaseType__c === 'Subscription',
    )

    // Differentiation between subscriptionId and subscriptionIds:
    // SubscriptionReferenceId__c is present only for child orders,
    // not for the parent order (the order in which the subscription was purchased).
    // When you purchase a subscription, the subscription object does not exist yet
    // until the parent order is shipped; at that point, the subscription object
    // generates the child orders.
    // Parent orders can have multiple associated subscriptions, so in that case, link to subscription history.
    //
    // For the "Manage Subscription" CTA:
    // - If order.subscriptionId is present, link to the subscription details page for that ID.
    // - If not, but order.subscriptionIds is a non-empty array, link to the subscription history page.
    const hasSubscriptionItems = subscriptionItems?.length > 0
    const hasSubscriptionIds =
        Array.isArray(order?.subscriptionIds) && order.subscriptionIds.length > 0

    return (
        <>
            <Text {...styles.orderNumberHeader}>
                <FormattedMessage
                    id="account_order_detail.label.order_number"
                    defaultMessage="Order number: {orderNumber}"
                    values={{
                        orderNumber: order?.orderNumber || params.orderNo,
                    }}
                />
            </Text>
            {returnSettings?.[deliveryCountryCode] === 'No return' &&
            (status === SFCC_ORDER_STATUS.SHIPPED || status === SFCC_ORDER_STATUS.DELIVERED) ? (
                <Flex {...styles.expiredNoReturnContainer}>
                    <Flex {...styles.expiredReturnLabelContainer}>
                        <Text {...styles.expiredReturnTitle}>
                            <FormattedMessage
                                id="account_order_detail.expired_return.title"
                                defaultMessage="Customer Service"
                            />
                        </Text>
                        <Text {...styles.expiredReturnDescription}>
                            <FormattedMessage
                                id="account_order_detail.label.no_return.description"
                                defaultMessage="If you need assistance please contact Customer Service."
                            />
                        </Text>
                    </Flex>
                    <Flex {...styles.expiredReturnButtonsContainer}>
                        <Button
                            as={Link}
                            to={buildUrl(routingObject.CONTACT_US_PATH)}
                            variant="primary"
                            {...styles.expiredReturnButton}
                        >
                            <FormattedMessage
                                id="home.link.contact_us"
                                defaultMessage="Contact us"
                            />
                        </Button>
                        <Button
                            as={Link}
                            to={buildUrl(routingObject.FAQ_PATH)}
                            variant="secondary"
                            {...styles.expiredReturnButton}
                        >
                            <FormattedMessage
                                id="account_order_detail.label.expired_return.secondButton"
                                defaultMessage="Read FAQs"
                            />
                        </Button>
                        <Button
                            onClick={handleReturnPolicy}
                            variant="secondary"
                            {...styles.expiredReturnButton}
                        >
                            <FormattedMessage
                                id="account_order_detail.label.expired_return.thirdButton"
                                defaultMessage="Go to return policy"
                            />
                        </Button>
                    </Flex>
                </Flex>
            ) : (
                <></>
            )}
            <SimpleGrid {...styles.stackWrapper}>
                <Flex
                    direction={{base: 'column', md: 'row'}}
                    justify="space-between"
                    align={{base: 'stretch', md: 'stretch'}}
                    w="100%"
                >
                    {/* LEFT SECTION (date + status) */}
                    <Stack spacing={remCalc(8)} flex="1" justify="flex-start">
                        {/* Date */}
                        {order?.orderedDate && (
                            <Text {...styles.bodyMedium} color="text.default">
                                <FormattedMessage
                                    id="account_order_detail.label.info.date"
                                    defaultMessage="<b>Date:</b> {date}"
                                    values={{
                                        date: formatDate(new Date(order.orderedDate), {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        }),
                                        b: (boldString) => <b>{boldString}</b>,
                                    }}
                                />
                            </Text>
                        )}

                        {/* Status + Mobile Button */}
                        <Flex
                            justify={{base: 'space-between', md: 'flex-start'}}
                            align="center"
                            flexWrap={{base: 'wrap', md: 'nowrap'}}
                            gap={remCalc(8)}
                            w="100%"
                        >
                            <Flex gap={remCalc(8)} align="center">
                                <Text {...styles.bodyMedium} fontWeight="bold" color="text.default">
                                    <FormattedMessage
                                        id="account_order_detail.label.status"
                                        defaultMessage="Status:"
                                    />
                                </Text>
                                <InformativeLabel
                                    label={translatedStatus}
                                    width="fit-content"
                                    variant={getInfoLabelVariantByOmsStatus(status)}
                                />
                            </Flex>

                            {/* Cancel Order Button — only on mobile */}
                            {!cancellationDate && isOrderCancellable && (
                                <Box display={{base: 'block', md: 'none'}}>
                                    <Button
                                        {...styles.tertiaryButtonLine}
                                        onClick={onCancelRequest}
                                    >
                                        <FormattedMessage
                                            id="account_order_detail.label.cancel_order"
                                            defaultMessage="Cancel Order"
                                        />
                                    </Button>
                                </Box>
                            )}
                        </Flex>
                    </Stack>
                    {/* DESKTOP Cancel Order aligned vertically center between date and status */}
                    {!cancellationDate && isOrderCancellable ? (
                        <Flex
                            display={{base: 'none', md: 'flex'}}
                            align="flex-start"
                            justify="center"
                        >
                            <Button {...styles.tertiaryButtonLine} onClick={onCancelRequest}>
                                <FormattedMessage
                                    id="account_order_detail.label.cancel_order"
                                    defaultMessage="Cancel Order"
                                />
                            </Button>
                        </Flex>
                    ) : (
                        <></>
                    )}
                </Flex>

                <SimpleGrid columns={{base: 1, md: 4}} gap={remCalc(24)}>
                    <Box>
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            <FormattedMessage
                                id="account_order_detail.label.delivery_method"
                                defaultMessage="Delivery Method"
                            />
                        </Text>
                        <Text {...styles.bodyMedium}>{deliveryMethod}</Text>
                    </Box>

                    <Box data-cs-mask>
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            <FormattedMessage
                                id="account_order_detail.label.delivery_address"
                                defaultMessage="Delivery Address"
                            />
                        </Text>
                        <Text {...styles.bodyMedium}>{deliveryAddress}</Text>
                    </Box>

                    <Box data-cs-mask>
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            <FormattedMessage
                                id="account_order_detail.label.billing_address"
                                defaultMessage="Billing Address"
                            />
                        </Text>
                        <Text {...styles.bodyMedium}>{billingAddress}</Text>
                    </Box>

                    {paymentType && (
                        <Box>
                            <Text fontWeight="bold">
                                <FormattedMessage
                                    id="account_order_detail.label.payment_method"
                                    defaultMessage="Payment Method"
                                />
                            </Text>
                            <Text>{paymentType}</Text>
                        </Box>
                    )}
                </SimpleGrid>
                <Text {...styles.bodyMedium} color="text.default">
                    {cancellationDate ? (
                        <FormattedMessage
                            id="account_order_detail.label.return_cancel_note"
                            defaultMessage="<b>This order has been cancelled on {cancellationDate}. {contactUs}</b>"
                            values={{
                                cancellationDate: formatDate(new Date(cancellationDate), {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                }),
                                contactUs: (
                                    <Button
                                        as={Link}
                                        {...styles.tertiaryButton}
                                        verticalAlign={'baseline'}
                                        to={buildUrl(routingObject.CONTACT_US_PATH)}
                                    >
                                        <FormattedMessage
                                            id="account_order_detail.label.contact_us"
                                            defaultMessage="Contact us"
                                        />
                                    </Button>
                                ),
                                b: (boldString) => <b>{boldString}</b>,
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="account_order_detail.label.return_note"
                            defaultMessage="<b>Returns:</b> You have 30 days from the delivery of your order to change
                your mind and return the products you purchased."
                            values={{
                                b: (boldString) => <b>{boldString}</b>,
                            }}
                        />
                    )}
                </Text>
                {(!cancellationDate || order?.subscriptionId || hasSubscriptionIds) && (
                    <Flex gap={remCalc(16)} wrap="wrap">
                        {!cancellationDate && (
                            <Button
                                as={Link}
                                variant="secondary"
                                width={{base: '100%', md: 'fit-content'}}
                                to={buildUrl(routingObject.CONTACT_US_PATH)}
                                whiteSpace="normal"
                                textAlign="center"
                            >
                                <FormattedMessage
                                    id="account_order_detail.label.button.expert"
                                    defaultMessage="Get in Touch with an Expert"
                                />
                            </Button>
                        )}
                        {hasSubscriptionItems && order?.subscriptionId && (
                            <Button
                                as={Link}
                                variant="primary"
                                width={{base: '100%', md: 'fit-content'}}
                                to={`/${localeId}/account/subscription-details/${order?.subscriptionId}`}
                            >
                                <FormattedMessage
                                    id="account_order_detail.label.button.manage.subscription"
                                    defaultMessage="Manage Subscription"
                                />
                            </Button>
                        )}
                        {hasSubscriptionItems &&
                            !order?.subscriptionId &&
                            hasSubscriptionIds &&
                            (Array.isArray(order?.subscriptionIds) &&
                            order.subscriptionIds.length === 1 ? (
                                <Button
                                    as={Link}
                                    variant="primary"
                                    width={{base: '100%', md: 'fit-content'}}
                                    to={`/${localeId}/account/subscription-details/${order.subscriptionIds[0]}`}
                                >
                                    <FormattedMessage
                                        id="account_order_detail.label.button.manage.subscription"
                                        defaultMessage="Manage Subscription"
                                    />
                                </Button>
                            ) : (
                                <Button
                                    as={Link}
                                    variant="primary"
                                    width={{base: '100%', md: 'fit-content'}}
                                    to={`/${localeId}/account/subscription-history`}
                                >
                                    <FormattedMessage
                                        id="account_order_detail.label.button.manage.subscription"
                                        defaultMessage="Manage Subscription"
                                    />
                                </Button>
                            ))}
                    </Flex>
                )}
            </SimpleGrid>
        </>
    )
}
OrderInfo.propTypes = {
    order: PropTypes.object,
    isOrderCancelled: PropTypes.bool,
    onCancelRequest: PropTypes.func,
}
export default OrderInfo
